<template>
  <Modal
    v-model="dialogVisible"
    class-name="appeal-reason-view-dialog"
    :title="dialogTitle"
    width="980"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <Spin v-if="loading" fix />
    <div v-else-if="loadError" class="appeal-reason-view-error">
      {{ loadError }}
    </div>
    <div v-else class="appeal-reason-view-body">
      <div class="appeal-reason-field">
        <div class="appeal-reason-label">申诉理由</div>
        <div class="appeal-reason-value">{{ detail.appealReason }}</div>
      </div>
      <div class="appeal-reason-field">
        <div class="appeal-reason-label">发起时间</div>
        <div class="appeal-reason-value">{{ detail.submittedAt }}</div>
      </div>
      <div class="appeal-reason-field">
        <div class="appeal-reason-label">附件（{{ detail.attachments.length }}）</div>
        <div v-if="!detail.attachments.length" class="appeal-reason-value">
          无附件
        </div>
        <ul v-else class="appeal-reason-attachment-list">
          <li
            v-for="(file, index) in detail.attachments"
            :key="`${file.id}-${index}`"
          >
            <a
              v-if="file.fileUrl"
              :href="file.fileUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="appeal-reason-attachment-link"
            >
              {{ file.name }}
            </a>
            <span v-else>{{ file.name }}</span>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <Button @click="handleBackToRecord">返回审核记录</Button>
      <Button @click="handleClose">关闭</Button>
    </template>
  </Modal>
</template>

<script>
import { getTtMediaReviewAppeal } from '@/api/short-review'
import {
  mapAppealDetailFromApi,
  formatTtVersionFromNumber
} from '../../utils/mapAppealDetail'
const EMPTY_DETAIL = {
  appealReason: '—',
  submittedAt: '—',
  attachments: []
}

export default {
  name: 'AppealReasonViewDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    },
    /** 不传则取最近送审版本（与 3.md ttVersion 说明一致） */
    ttVersion: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      loadError: '',
      detail: { ...EMPTY_DETAIL },
      appealVersionLabel: '—'
    }
  },
  computed: {
    dialogTitle() {
      const name = (this.row && this.row.playName) || '—'
      return `查看申诉理由 · ${name} / ${this.appealVersionLabel}`
    },
    reviewRecordId() {
      const row = this.row || {}
      if (row.id == null || row.id === '') return null
      return row.id
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.fetchAppealDetail()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    fetchAppealDetail() {
      const id = this.reviewRecordId
      if (id == null) {
        this.loadError = '缺少审核记录，无法加载申诉详情'
        this.detail = { ...EMPTY_DETAIL }
        return
      }
      this.loading = true
      this.loadError = ''
      const params = {}
      const tt = Number(this.ttVersion)
      if (!Number.isNaN(tt) && tt > 0) {
        params.ttVersion = tt
      }
      getTtMediaReviewAppeal(id, params)
        .then((res) => {
          if (res.code === 200 && res.data) {
            const mapped = mapAppealDetailFromApi(res.data)
            if (!mapped) {
              this.loadError = '暂无申诉详情'
              this.detail = { ...EMPTY_DETAIL }
              this.appealVersionLabel = formatTtVersionFromNumber(
                res.data.ttVersion
              )
              return
            }
            this.detail = mapped
            this.appealVersionLabel = mapped.version || '—'
            return
          }
          this.loadError = res.message || '获取申诉详情失败'
          this.detail = { ...EMPTY_DETAIL }
        })
        .catch((err) => {
          this.loadError =
            (err && err.message) || '获取申诉详情失败'
          this.detail = { ...EMPTY_DETAIL }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleBackToRecord() {
      this.dialogVisible = false
      this.$emit('back-to-record')
    },
    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.appeal-reason-view-body {
  box-sizing: border-box;
}

.appeal-reason-view-error {
  font-size: 14px;
  line-height: 22px;
  color: #ed4014;
}

.appeal-reason-field {
  margin-bottom: 20px;
}

.appeal-reason-field:last-child {
  margin-bottom: 0;
}

.appeal-reason-label {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #8a98a8;
}

.appeal-reason-value {
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-word;
}

.appeal-reason-attachment-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 22px;
  color: #303133;
}

.appeal-reason-attachment-link {
  color: #2d8cf0;
  word-break: break-all;
}

.appeal-reason-attachment-link:hover {
  color: #57a3f3;
  text-decoration: underline;
}
</style>

<style>
.appeal-reason-view-dialog {
  display: flex;
  align-items: center;
  justify-content: center;
}

.appeal-reason-view-dialog .ivu-modal {
  top: 0;
}

.appeal-reason-view-dialog .ivu-modal-content {
  display: flex;
  flex-direction: column;
  height: auto;
  max-height: calc(100vh - 48px);
  box-sizing: border-box;
}

.appeal-reason-view-dialog .ivu-modal-header {
  flex-shrink: 0;
  height: 76px;
  box-sizing: border-box;
  padding: 0 24px;
  border-bottom: 1px solid #e8eaec;
  display: flex;
  align-items: center;
}

.appeal-reason-view-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #263f58;
}

.appeal-reason-view-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

.appeal-reason-view-dialog .ivu-modal-body {
  flex: 0 1 auto;
  height: auto;
  min-height: 0;
  padding: 24px;
  overflow: auto;
  box-sizing: border-box;
  position: relative;
}

.appeal-reason-view-dialog .ivu-modal-footer {
  flex-shrink: 0;
  height: 66px;
  box-sizing: border-box;
  padding: 0 24px;
  border-top: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.appeal-reason-view-dialog .ivu-modal-footer .ivu-btn {
  height: 35px;
  padding: 8px 15px;
  font-size: 13px;
  line-height: 17px;
}

.appeal-reason-view-dialog .ivu-modal-footer .ivu-btn + .ivu-btn {
  margin-left: 8px;
}
</style>
